import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FORM_INPUT_REQS } from "src/app/app.config";
import { ConfigAccessory, ConfigAccessoryOption } from "src/app/clearpath-module/models/config-accessory";

@Component({
  selector: "app-accessory-form",
  templateUrl: "./accessory-form.component.html",
  styleUrls: ["./accessory-form.component.scss"]
})
export class AccessoryFormComponent implements OnInit, OnDestroy {

  public REQS = FORM_INPUT_REQS;
  private unsubscribe$ = new Subject();
  @Input() accessory: ConfigAccessory;
  @Input() index: number;
  @Output() formChanges = new EventEmitter();
  accessoryForm: UntypedFormGroup;

  constructor(private formBuilder: UntypedFormBuilder) { }

  ngOnInit() {
    if (!this.accessory.details) {
      this.accessory.details = [];
    }
    if (!this.accessory.options) {
      this.accessory.options = [];
    }
    this.initForm();
    this.watchForm();
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initForm() {
    this.accessoryForm = this.formBuilder.group({
      name: [this.accessory.name, Validators.required],
      tagLine: [this.accessory.tagLine, Validators.required],
      details: this.formBuilder.array(this.accessory.details, {updateOn: "blur"}),
      pbsName: [this.accessory.pbsName],
      pbsType: {value: "Protection", disabled: true},
      options: this.formBuilder.array(this.accessory.options?.map(o => this.buildOptionGroup(o)))
    });
  }

  private buildOptionGroup(option: Partial<ConfigAccessoryOption> = {}): UntypedFormGroup {
    return this.formBuilder.group({
      name: [option.name ?? "", Validators.required],
      label: [option.label ?? "", Validators.required],
      pbsName: [option.pbsName ?? ""],
      price: [option.price ?? 0],
      cost: [option.cost ?? 0],
      defaultSelection: [option.defaultSelection ?? false],
      details: this.formBuilder.array((option.details || []).map(d => this.formBuilder.control(d)))
    }, {updateOn: "blur"});
  }

  watchForm() {
    this.accessoryForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(accessoryForm => {
        this.formChanges.emit(accessoryForm);
      });
  }

  createDetail() {
    this.detailsAsFormArray.push(this.formBuilder.control(""));
  }

  removeDetail(index) {
    this.detailsAsFormArray.removeAt(index);
  }

  createOption() {
    this.optionsAsFormArray.push(this.buildOptionGroup());
  }

  removeOption(index) {
    this.optionsAsFormArray.removeAt(index);
  }

  optionDetails(optionIndex: number): UntypedFormArray {
    return this.optionsAsFormArray.at(optionIndex).get("details") as UntypedFormArray;
  }

  addOptionDetail(optionIndex: number) {
    this.optionDetails(optionIndex).push(this.formBuilder.control(""));
  }

  removeOptionDetail(optionIndex: number, detailIndex: number) {
    this.optionDetails(optionIndex).removeAt(detailIndex);
  }

  get detailsAsFormArray() {
    return this.accessoryForm.get("details") as UntypedFormArray;
  }

  get details(): UntypedFormArray {
    return this.accessoryForm.get("details").value;
  }

  get optionsAsFormArray() {
    return this.accessoryForm.get("options") as UntypedFormArray;
  }

  get options(): UntypedFormArray {
    return this.accessoryForm.get("options").value;
  }
}
